import { Inject, Injectable, Optional, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';

import { Observable, BehaviorSubject, of, from } from 'rxjs';
import { map, share, mergeMap } from 'rxjs/operators';


import { ApiService } from './api.service';
import { CookieService } from './cookie.service';
import { ProfileService } from './profile.service';
import { UserAuth } from 'src/app/core/models/user-auth.model';
import { User } from 'src/app/core/models/user.model';
import { ReactNativeService } from './react-native.service';
import { isPlatformServer } from '@angular/common';

export interface AuthServiceStatus {
    isSigned: boolean
};

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    protected readonly TOKEN_NAME_COOKIE: string = 'user';
    protected readonly COOKIE_DAYS_EXPIRE: number = 365;

    protected static isLoaded: boolean = false;
    protected subjectReady: BehaviorSubject<boolean> = new BehaviorSubject(false);
    protected subjectStatus: BehaviorSubject<AuthServiceStatus> = new BehaviorSubject({
        isSigned: false,
    });

    public user: User = new User();

    constructor(
        @Optional() @Inject(PLATFORM_ID) private platform: Object,
        private api: ApiService,
        private router: Router,
        private profileService: ProfileService,
        private cookieService: CookieService,
        private reactNativeService: ReactNativeService,
    ) {
        if (AuthService.isLoaded && !isPlatformServer(this.platform)) {
            return;
        }
        AuthService.isLoaded = true;

        this.userLoad();

        if (!this.user.isValid() && this.user.hasToken()) {
            this.getActualProfileData().then((data: any) => {}).catch(e => {});
        } else {
            const isValid = this.user.isValid();

            if (isValid) {
                this.api.updateToken(this.user.getToken());
            }

            this.subjectReady.next(true);
            this.subjectStatus.next({
                isSigned: isValid,
            });
        }

    }

    login({email, password}): Observable<UserAuth> {
        return this.api.post('/auth/login', {email, password}).pipe(map((data: {data: UserAuth}) => {
            let profile: User = "data" in data && data.data && 'user' in data.data ? data.data.user : null;
            let token: string = "data" in data && data.data && 'token' in data.data ? data.data.token : '';

            this.api.updateToken(token);
            this.cookieService.set(this.TOKEN_NAME_COOKIE, token, 365, '/');

            this.userStore(profile, token);
            this.subjectReady.next(true);
            this.subjectStatus.next({
                isSigned: this.user.isValid(),
            });

            return data.data;
        }));
    }

    /**
     * Performs a proper logout to the system, which should make token invalidated
     * @param {boolean} skipToken Skip the logout API request
     */
    logout(skipToken: boolean = false): Observable<any> {
        let final: () => any = () => {
            this.api.updateToken('');
            this.user = new User();
            this.cookieService.delete(this.TOKEN_NAME_COOKIE);
            this.subjectReady.next(true);
            this.subjectStatus.next({
                isSigned: this.user.isValid(),
            });
        };

        if (skipToken) {
            final();
            return of({}).pipe(map((data: any) => {
                return data;
            }));
        }

        return from(this.reactNativeService.onLogout()).pipe(mergeMap(val =>
            this.api.post('/auth/logout')
                .pipe(map((data: any) => {
                    final();
                    return data;
                }))
        ));
    }

    /**
     * Send an email with reset link
     * @param
     */
    forgot({email}): Observable<UserAuth> {
        return this.api.post('/auth/forgot', {email});
    }

    /**
     * Reset password
     * @param
     */
    reset({email, password, token}): Observable<UserAuth> {
        return this.api.post('/auth/reset', {email, password, token});
    }

    /**
     * Get authentication status changes over time
     */
    getStatus(): Observable<AuthServiceStatus> {
        return this.subjectStatus.asObservable().pipe(share());
    }

    /**
     * Returns status of the authentication process, i.e. is in progress (false) or finished (true)
     */
    getAuthReady(): Observable<boolean> {
        return this.subjectReady.asObservable().pipe(share());
    }

    /**
     * Check if loaded user token (individual for each tab instance with same domain) is
     * different from saved in Cookie token (visible by all tab/window instances)
     */
    hasTokenChanged(): boolean {
        const userToken: string = this.user ? (this.user.getToken() || '') : '';
        const cookieToken: string = this.cookieService.get(this.TOKEN_NAME_COOKIE) || '';
        return userToken !== cookieToken;
    }

    /**
     * Check if there is a Cookie set with a token
     */
    hasCookieToken(): boolean {
        const cookieToken: string = this.cookieService.get(this.TOKEN_NAME_COOKIE) || '';
        return cookieToken && cookieToken.length > 0;
    }

    /**
     * Update profile data using server
     */
    getActualProfileData(): Promise<boolean> {
        return new Promise((resolve, rejected) => {

            this.api.updateToken(this.user.getToken());

            let onError: () => void = (): void => {
                this.logout(true).subscribe((data: any) => {
                    this.subjectReady.next(true);
                    this.subjectStatus.next({
                        isSigned: this.user.isValid(),
                    });

                    // In order to force NavigationEnd events firing
                    setTimeout(() => {
                        this.router.navigate(['/']);
                        resolve(this.user.isValid());
                    });
                }, error => {
                    rejected(error);
                });
            };

            this.profileService.getProfile().subscribe(profile => {
                this.userStore(profile.data);
                this.subjectReady.next(true);
                this.subjectStatus.next({
                    isSigned: this.user.isValid(),
                });

                resolve(this.user.isValid());
            }, error => {
                onError();
                rejected(error);
            });
        });
    }


    /**
     * Get User Token
     */
    protected userLoad(): void {
        const token: string = this.cookieService.get(this.TOKEN_NAME_COOKIE);
        this.user.setToken(token);
    }

    protected userStore(profile: User, token: string = ''): void {
        this.user.id = profile.id || 0;
        this.user.name_first = profile.name_first || '';
        this.user.name_middle = profile.name_middle || '';
        this.user.name_last = profile.name_last || '';
        this.user.initials = profile.initials || '';
        this.user.email = profile.email || '';
        this.user.phone = profile.phone || '';
        this.user.address = profile.address || '';
        this.user.description = profile.description || '';
        this.user.status = profile.status || 'inactive';
        this.user.photo = profile.photo || '';
        this.user.roles = profile.roles || [];
        this.user.permissions = profile.permissions || [];
        this.user.roles_count = this.user.roles_count || 0;

        this.user.token = token || this.user.token || '';
        this.api.updateToken(this.user.getToken());
    }

}
